import React, { useState, useMemo } from "react";
import { useQuery } from "react-query";
import { Card, Select, Typography, Spin, Empty } from "antd";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  getRevenue,
  getContractsTimeline,
} from "../../../services/dashboard.service";
import { formatCurrency } from "../../../services/utils";
import moment from "moment";

const { Title } = Typography;
const { Option } = Select;

export default function AverageTicketChart() {
  const [months, setMonths] = useState(12);

  const { data: revenueData, isLoading: isLoadingRevenue } = useQuery(
    ["getRevenue", { months }],
    () => getRevenue({ months }),
    {
      refetchOnWindowFocus: false,
    }
  );

  const { data: contractsData, isLoading: isLoadingContracts } = useQuery(
    ["getContractsTimeline", months],
    () => getContractsTimeline(months),
    {
      refetchOnWindowFocus: false,
    }
  );

  const chartData = useMemo(() => {
    if (!revenueData?.data || !contractsData?.data) return [];

    const revenueByMonth = revenueData.data.reduce((acc: any, curr: any) => {
      acc[curr.month] = curr.liquidated || 0;
      return acc;
    }, {});

    const contractsByMonth = contractsData.data.reduce(
      (acc: any, curr: any) => {
        acc[curr.month] = curr.active_total || 0;
        return acc;
      },
      {}
    );

    // Merge keys (months) from both datasets to ensure alignment
    const allMonths = Array.from(
      new Set([
        ...Object.keys(revenueByMonth),
        ...Object.keys(contractsByMonth),
      ])
    );

    // Sort months? Assuming format is "MM/YYYY" or similar that might need custom sort,
    // but usually APIs return chronological. If string sort works for "YYYY-MM", great.
    // If it's "MMM/YY", we might rely on the array order from API which is usually sorted.
    // Let's assume the API returns sorted arrays and we map based on that.

    // Safer approach: Iterate over one of the arrays (e.g. revenue) and look up the other
    return revenueData.data.map((item: any) => {
      const month = item.month;
      const revenue = item.liquidated || 0;
      // Find matching contract data. Assuming format matches.
      const contractItem = contractsData.data.find(
        (c: any) => c.month === month
      );
      const activeContracts = contractItem ? contractItem.active_total : 0;

      const averageTicket = activeContracts > 0 ? revenue / activeContracts : 0;

      return {
        month,
        averageTicket,
        revenue,
        activeContracts,
      };
    });
  }, [revenueData, contractsData]);

  const isLoading = isLoadingRevenue || isLoadingContracts;

  return (
    <Card
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <Title level={5} style={{ margin: 0 }}>
              Ticket Médio
            </Title>
            <Typography.Text type="secondary">
              Receita / Contratos Ativos
            </Typography.Text>
          </div>
          <Select
            defaultValue={12}
            style={{ width: 120 }}
            onChange={(value) => setMonths(value)}
          >
            <Option value={6}>6 meses</Option>
            <Option value={12}>12 meses</Option>
            <Option value={24}>24 meses</Option>
          </Select>
        </div>
      }
      bordered={false}
      style={{ height: "100%" }}
    >
      {isLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 50 }}>
          <Spin />
        </div>
      ) : chartData.length > 0 ? (
        <div style={{ width: "100%", height: 300 }}>
          <ResponsiveContainer>
            <LineChart
              data={chartData}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="month"
                tickFormatter={(value: any) =>
                  `${moment(value).format("MM/YYYY")}`
                }
              />
              <YAxis tickFormatter={(value) => `R$ ${value}`} />
              <Tooltip
                labelFormatter={(value: any) =>
                  `${moment(value).format("MM/YYYY")}`
                }
                formatter={(value: any) => formatCurrency(Number(value) || 0)}
                labelStyle={{ color: "black" }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="averageTicket"
                name="Ticket Médio"
                stroke="#1890ff"
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <Empty description="Sem dados suficientes para cálculo" />
      )}
    </Card>
  );
}
